import { DateService } from '@rocheglobal/component-library-dot-com/src/services/date';
import { TranslationService } from '@rocheglobal/component-library-dot-com/src/services/translation';
import { StringService as StringHelper } from '@rocheglobal/component-library-dot-com/src/services/string/index';
import React, { useEffect, useRef, useState } from 'react';
import { HistoryService } from '@rocheglobal/component-library-dot-com/src/services/history';
import { LanguageService } from '../../services';
import { ExternalDataService } from '../../services/external-data';
import { Props } from '../types';
import { NewsCategory } from '../../services/external-data/types';
import { DateServiceTemp } from '../../services/date-time-formatter';

interface ListProps {
  year: string;
  isHidden: boolean;
}

interface ListResponse {
  canListen: boolean;
  Items: unknown[];
  Page: number;
  Total: number;
  translations?: {
    results: string;
  };
}

const List = (props: ListProps): JSX.Element => {
  const { year, isHidden } = props;
  const [state, setState] = useState({
    isFetching: false,
    items: [],
    total: 0,
    page: 1,
    canListen: false,
    translations: {
      results: '',
    },
    previousPage: 0,
  });
  const locale = LanguageService.getActiveLanguage();
  const container = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const { current }: { current: HTMLElement } = container;
    if (!current) {
      return;
    }

    current.parentElement.addEventListener('rochePaginationPageChange', (event: CustomEvent) => setState({ ...state, page: event.detail, previousPage: state.page }));
  }, [state.canListen]);

  useEffect(() => {
    if (isHidden || state.previousPage === state.page) {
      return;
    }

    setState((prevState) => ({ ...prevState, isFetching: true }));
    ExternalDataService.getNews({ category: NewsCategory.TEST, year, page: state.page })
      .then(async (response: ListResponse) => {
        const { Items, Page, Total } = response;
        const itemsWithDate = await Promise.all(Items.map(
          async (item: Record<string, unknown>) => {
            const ReleaseDateTime = DateServiceTemp.convertDateToTimeZone(item.ReleaseDateTime as string | Date, 'Europe/Berlin');
            return ({
              ...item,
              parsedDate: await DateService.formatDate(ReleaseDateTime as string | Date, 'dateFull'),
            });
          },
        ));
        setState({
          ...state,
          canListen: true,
          items: itemsWithDate,
          page: Page,
          previousPage: Page,
          total: Total,
          translations: {
            results: await TranslationService.translate('results'),
          },
        });
      })
      .finally(() => setState((prevState) => ({ ...prevState, isFetching: false })));
  }, [state.page, isHidden]);

  if (state.isFetching) {
    return (<roche-loading-spinner></roche-loading-spinner>);
  }

  if (!state.items.length) {
    return (<roche-empty-state></roche-empty-state>);
  }

  return (
    <div ref={container}>
      <roche-richtext>
        {state.total} {StringHelper.capitalize(state.translations?.results)}
      </roche-richtext>
      {
        state.items
          .map((item, index) => (
            <roche-teaser
              key={index}
              layout="teaser-variant-c-without-image"
              teaser-title={ExternalDataService.getLocalisedValue(item, 'Title', locale)}
              teaser-overline={item.parsedDate}
              teaser-url={
                LanguageService.getLocaleAwareLink(`test/releases/${item.Slug}`, LanguageService.getActiveLanguage())
              }
            />
          ))
      }
      {state.total / itemsPerPage > 1
        && <roche-pagination
          total-pages={Math.ceil(state.total / itemsPerPage)}
          active-page={state.page}
        ></roche-pagination>
      }
    </div>
  );
};

export const RocheTestReleases = ({ slot }: Props): JSX.Element => {
  const [years, setYears] = useState([]);
  const [activeYear, setActiveYear] = useState('');
  const tabs = useRef(null);

  useEffect(() => {
    ExternalDataService.getNewsYears({ category: NewsCategory.TEST })
      .then((response: string[]) => {
        const sortedYears = (Array.isArray(response) ? response : [])
          .sort()
          .reverse();

        setYears(sortedYears);
      });
  }, []);

  useEffect(() => {
    const { current } = tabs;
    if (!current) {
      return;
    }

    const callBack = (entries: MutationRecord[]): void => {
      setActiveYear((value) => {
        const active = entries.find((entry) => (entry.target as HTMLElement)?.id !== value);
        return (active?.target as HTMLElement)?.id || value;
      });
    };
    const observer = new MutationObserver(callBack);
    observer.observe(current, { attributeFilter: ['open'], childList: false, subtree: true });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [years]);

  if (years.length <= 0) {
    return null;
  }

  const startIndex = years
    .indexOf(HistoryService.getParsedHash(HistoryService.getActiveUrl().hash));

  return (
    <roche-accordion-tabs
      type="tabs"
      appearance="filter"
      slot={slot}
      start-position={startIndex}
      ref={tabs}
    >
      {years
        .map((year, index) => (
          <roche-accordion-tabs-item
            key={index}
            headline={year}
            id={year}
            update-on-content-change
          >
            <List
              year={year}
              isHidden={activeYear !== year}
            />
          </roche-accordion-tabs-item>
        ))}
    </roche-accordion-tabs>
  );
};
